import { BuildClass, clamp } from '../../../universal';
import { React, ReactDOM, _ } from '../../lib';
import { ConditionalObject } from './meta-types';
import { stubDiv } from './stubs';
export const Modal = (props) => {
    const elementRef = React.useRef(stubDiv);
    const [, setElement] = React.useState();
    React.useEffect(() => {
        const container = document.querySelector('#modal-container');
        if (!container) {
            console.error('No #modal-container defined');
            return _.noop;
        }
        const availableElement = container.querySelector('.available');
        const modalElement = availableElement instanceof HTMLDivElement
            ? availableElement
            : document.createElement('div');
        if (!availableElement) {
            container.appendChild(modalElement);
        }
        elementRef.current = modalElement;
        setElement(modalElement);
        return () => {
            modalElement.className = 'ui5-modal available';
        };
    }, []);
    React.useEffect(() => {
        if (elementRef.current) {
            elementRef.current.className = BuildClass(['ui5-modal', props.className]);
        }
    }, [props.className]);
    if (!elementRef.current) {
        return React.createElement(React.Fragment, null);
    }
    return ReactDOM.createPortal(props.children, elementRef.current);
};
const getModalAnchorSide = (options) => {
    if (!options.adjacent) {
        return options.isVerticallyFlipped ? 'top' : 'bottom';
    }
    if (options.heightDown >= options.requiredHeight) {
        return 'bottom';
    }
    if (options.heightUp >= options.requiredHeight) {
        return 'top';
    }
    if (options.widthRight >= options.requiredWidth) {
        return 'right';
    }
    if (options.widthLeft >= options.requiredWidth) {
        return 'left';
    }
    return options.heightDown >= options.heightUp ? 'bottom' : 'top';
};
const ModalAnchoredComponent = (props, ref) => {
    const elementRef = React.useRef(stubDiv);
    const alignX = props.alignX ?? 'left';
    const rect = props.mountElement.getBoundingClientRect();
    const gap = props.gap ?? 6;
    const requestedWidth = props.width ?? rect.width;
    const viewportWidth = Math.max(0, window.innerWidth - (props.adjacent ? gap * 2 : 0));
    const width = Math.min(requestedWidth, viewportWidth);
    const heightDown = clamp(window.innerHeight - rect.bottom - (props.adjacent ? gap : 0), 0, props.maxHeight);
    const heightUp = clamp(rect.top - (props.adjacent ? gap : 0), 0, props.maxHeight);
    const widthRight = window.innerWidth - rect.right - gap;
    const widthLeft = rect.left - gap;
    const flipThreshold = props.flipHeightThreshold ?? props.maxHeight;
    const isVerticallyFlipped = (props.flipByDefault ?? false)
        ? !(heightUp < flipThreshold && heightDown > heightUp)
        : heightDown < flipThreshold && heightUp > heightDown;
    const side = getModalAnchorSide({
        adjacent: props.adjacent ?? false,
        heightDown,
        heightUp,
        widthRight,
        widthLeft,
        requiredHeight: props.maxHeight,
        requiredWidth: width,
        isVerticallyFlipped,
    });
    const height = side === 'left' || side === 'right'
        ? Math.min(props.maxHeight, window.innerHeight)
        : side === 'top'
            ? heightUp
            : heightDown;
    const leftMap = {
        left: rect.left,
        right: rect.right - width,
        center: (rect.left + rect.right - width) / 2,
    };
    const left = side === 'right'
        ? rect.right + gap
        : side === 'left'
            ? rect.left - width - gap
            : clamp(leftMap[alignX], 0, window.innerWidth - width);
    const top = side === 'left' || side === 'right'
        ? clamp((rect.top + rect.bottom - height) / 2, 0, window.innerHeight - height)
        : side === 'top'
            ? rect.top - height - (props.adjacent ? gap : 1)
            : rect.bottom + (props.adjacent ? gap : -1);
    React.useImperativeHandle(ref, () => ({
        getElement: () => elementRef.current,
    }));
    return (React.createElement(Modal, null,
        React.createElement("div", { ref: elementRef, className: BuildClass({
                'ui5-modal-inner': true,
                [BuildClass(props.className)]: true,
                hidden: props.hidden,
                flipped: side === 'top',
            }), style: {
                left: `${left}px`,
                top: `${top}px`,
                width: `${width}px`,
                maxHeight: `${height}px`,
                minWidth: `${width}px`,
                overflowY: props.adjacent &&
                    (height < props.maxHeight || props.contentOverflows === true)
                    ? 'auto'
                    : undefined,
            }, onMouseDown: e => {
                e.stopPropagation();
                e.preventDefault();
            } }, ConditionalObject(!props.hidden, props.children))));
};
export const ModalAnchored = React.forwardRef(ModalAnchoredComponent);
//# sourceMappingURL=modal.js.map